import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { PageResponse } from '@models/api';
import {
  type EsimInventoryResponse,
  type GetEsimInventoriesParams,
  getEsimInventories,
} from '@apis/esim';

import { dropEmptyValues } from '../../utils/common';

const LIST_KEYS = [
  'stockId',
  'esimType',
  'esimStatus',
  'expireAtFrom',
  'expireAtTo',
  'parameterType',
  'parameterValue',
] as const;

type ListKey = (typeof LIST_KEYS)[number];

type UseInventoryListArgs = {
  page: number;
  pageSize: number;
  searchParam: GetEsimInventoriesParams;
};

/**
 * Inventory list query hook.
 *
 * - Builds a stable queryKey based on pagination + selected filter fields.
 * - Removes empty filter values before calling the API to keep requests consistent.
 * - Keeps previous page data while fetching the next page to reduce UI flicker.
 */
export const useInventoryList = ({ page, pageSize, searchParam }: UseInventoryListArgs) => {
  /**
   * API request params:
   * Remove empty values from the filter object while keeping valid values (e.g. 0, false if applicable).
   * dropEmptyValues must be implemented so that it does NOT drop valid falsy values unintentionally.
   */
  const cleaned = useMemo(
    () => dropEmptyValues(searchParam, [...LIST_KEYS]),
    [searchParam],
  );

  /**
   * Query key parts:
   * Avoid using the filter object directly in the key to prevent cache misses caused by referential changes.
   */
  const keyParts = useMemo(
    () => LIST_KEYS.map((k: ListKey) => (cleaned as Record<ListKey, unknown>)[k] ?? null),
    [cleaned],
  );

  return useQuery<PageResponse<EsimInventoryResponse>, Error>({
    queryKey: ['esimInventories', page, pageSize, ...keyParts],
    queryFn: () =>
      getEsimInventories({
        pageNo: page,
        pageSize,
        ...cleaned,
      }),

    // React Query v5: keep previous data during pagination to reduce flicker.
    placeholderData: (prev) => prev,

    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
};
